import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  authErrorCode,
  changePasswordAndNotify,
  changePasswordErrorKey,
  passwordMeetsPolicy,
} from "@heraion/shared";
import { apiErrorText, FieldLabel, makeSettingsCardStyles, NewPasswordFields, resolveTheme, TextField, webTapFloor, type UiTheme } from "@heraion/ui";
import { apiFetch } from "./api";
import { PrivacyActions, type PrivacyBusy } from "./AccountPrivacyActions";
import { changePassword } from "./auth/cognito";
import { useAuth } from "./auth/AuthContext";
import { useThemePalette } from "./theme";
import { useShellProfile } from "./ThemeProfileBridge";
import { stateAria } from "@heraion/ui";

// Settings → Account on the web console: email (read-only), display name,
// change password, data export, age-declaration withdrawal, account deletion,
// sign out. Mirror of the mobile SettingsAccount. Name is a profile scalar
// (get-merge-PUT); password goes through Cognito; the privacy card lives in
// AccountPrivacyActions.tsx.

export function AccountScreen({
  focusName = false,
  onFocusTaken,
}: {
  /** Focus the name field when it turns true (the bell, #178); `onFocusTaken` clears the ask. */
  focusName?: boolean;
  onFocusTaken?: () => void;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { state, signOut } = useAuth();
  const { doc, saveField } = useShellProfile();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  // Lifted so sign-out waits for a delete or withdrawal in flight.
  const [busy, setBusy] = useState<PrivacyBusy>(null);

  const email = state.status === "signed_in" ? state.user.email : "—";

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <View style={card.card}>
        <Text style={card.cardLabel}>
          {t("mobile.profile.labelEmail", { defaultValue: "EMAIL" })}
        </Text>
        <Text style={card.cardValue}>{email}</Text>
      </View>

      <NameEditor
        theme={theme}
        initial={(doc?.["name"] as string | undefined) ?? ""}
        onSave={(name) => saveField("name", name)}
        focus={focusName}
        onFocusTaken={onFocusTaken}
      />

      <ChangePassword theme={theme} />

      <PrivacyActions theme={theme} onSignedOut={signOut} busy={busy} setBusy={setBusy} />

      <Pressable
        accessibilityRole="button"
        disabled={busy !== null}
        style={({ pressed }) => [
          card.secondaryAction,
          busy !== null && card.disabled,
          pressed && card.secondaryActionPressed,
        ]}
        onPress={signOut}
      >
        <Text style={card.secondaryActionText}>
          {t("mobile.profile.signOut", { defaultValue: "Sign out" })}
        </Text>
      </Pressable>
    </ScrollView>
  );
}

function NameEditor({
  theme,
  initial,
  onSave,
  focus = false,
  onFocusTaken,
}: {
  theme: UiTheme;
  initial: string;
  onSave: (name: string) => Promise<void>;
  focus?: boolean;
  onFocusTaken?: () => void;
}) {
  const { t } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (!focus) return undefined;
    // The bell's sheet (a Modal) closes in the same tick as the ask, and on
    // closing hands the focus back to its trigger some 150-400 ms later. So
    // the field asks again every 100 ms for a second, and only ever takes the
    // focus from the sheet or the bell: the athlete touching or typing
    // anywhere else ends it. The ask is taken at the end, not at the first try.
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      window.removeEventListener("pointerdown", onTouch, true);
      window.removeEventListener("keydown", onTouch, true);
      onFocusTaken?.();
    };
    const target = () => inputRef.current as unknown as HTMLElement | null;
    const onTouch = (e: Event) => {
      if (e.target !== target()) finish();
    };
    const try_ = () => {
      const el = target();
      if (el && document.activeElement !== el) el.focus();
    };
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= 1000) finish();
      else try_();
    }, 100);
    window.addEventListener("pointerdown", onTouch, true);
    window.addEventListener("keydown", onTouch, true);
    try_();
    return () => {
      done = true;
      clearInterval(timer);
      window.removeEventListener("pointerdown", onTouch, true);
      window.removeEventListener("keydown", onTouch, true);
    };
  }, [focus, onFocusTaken]);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const trimmed = value.trim();
  const dirty = trimmed !== initial.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 80;

  const save = async () => {
    if (saving || !dirty || !valid) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onSave(trimmed);
      setSaved(true);
    } catch (err) {
      setError(apiErrorText(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.account.nameLabel", { defaultValue: "NAME" })}
      </Text>
      <View style={styles.fieldRow}>
        <TextField
          ref={inputRef}
          theme={theme}
          style={styles.input}
          accessibilityLabel={t("mobile.account.nameLabel", { defaultValue: "NAME" })}
          maxLength={80}
          value={value}
          onChangeText={(v) => {
            setValue(v);
            setSaved(false);
          }}
          placeholder={t("mobile.account.nameLabel", { defaultValue: "NAME" })}
          editable={!saving}
        />
        <Pressable
          accessibilityRole="button"
          disabled={!dirty || !valid || saving}
          onPress={() => void save()}
          style={({ pressed }) => [
            styles.inlineBtn,
            (!dirty || !valid || saving) && styles.inlineBtnDisabled,
            pressed && styles.inlineBtnPressed,
          ]}
        >
          {saving ? (
            <ActivityIndicator color={theme.palette.inkOnBrand} />
          ) : (
            <Text style={styles.inlineBtnText}>
              {t("mobile.common.save", { defaultValue: "Save" })}
            </Text>
          )}
        </Pressable>
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {saved ? <Text style={styles.ok}>{t("mobile.common.save", { defaultValue: "Save" })} ✓</Text> : null}
    </View>
  );
}

function ChangePassword({ theme }: { theme: UiTheme }) {
  const { t, i18n } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const valid = oldPw.length > 0 && passwordMeetsPolicy(newPw) && newPw === confirmPw;

  const save = async () => {
    if (saving || !valid) return;
    setSaving(true);
    setError(null);
    setDone(false);
    // Every attempt puts the passwords back under cover (NewPasswordFields).
    setRevealed(false);
    try {
      await changePasswordAndNotify({
        changePassword: () => changePassword(oldPw, newPw),
        apiFetch,
        locale: i18n.language,
      });
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
      setDone(true);
    } catch (err) {
      // Never the provider's English: a mapped code has its own copy, anything
      // else gets the generic line.
      const key = changePasswordErrorKey(authErrorCode(err));
      setError(
        t(key ?? "mobile.account.actionFailed", {
          defaultValue: "That did not work. Try again in a moment.",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (
    value: string,
    setter: (v: string) => void,
    label: string,
    autoComplete: "current-password" | "new-password",
  ) => (
    <View>
      <FieldLabel theme={theme} style={styles.pwLabel}>
        {label}
      </FieldLabel>
      <TextField
        theme={theme}
        style={styles.pwInput}
        accessibilityLabel={label}
        value={value}
        onChangeText={(v) => {
          setter(v);
          setDone(false);
        }}
        secureTextEntry
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete={autoComplete}
        placeholder="•••••••••"
        editable={!saving}
      />
    </View>
  );

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.account.passwordLabel", { defaultValue: "CHANGE PASSWORD" })}
      </Text>
      {field(oldPw, setOldPw, t("mobile.account.currentPassword", { defaultValue: "Current password" }), "current-password")}
      <NewPasswordFields
        theme={theme}
        password={newPw}
        confirm={confirmPw}
        onChangePassword={(v) => {
          setNewPw(v);
          setDone(false);
        }}
        onChangeConfirm={(v) => {
          setConfirmPw(v);
          setDone(false);
        }}
        editable={!saving}
        revealed={revealed}
        onToggleReveal={() => setRevealed((r) => !r)}
      />
      {error ? (
        <Text style={styles.error} accessibilityRole="alert" accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
      {done ? (
        <Text style={styles.ok} accessibilityLiveRegion="polite">
          {t("mobile.account.passwordChanged", { defaultValue: "Password changed." })}
        </Text>
      ) : null}
      {saving ? (
        <Text style={styles.ok} accessibilityLiveRegion="polite">
          {t("auth.submitting")}
        </Text>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("mobile.account.passwordLabel", { defaultValue: "CHANGE PASSWORD" })}
        accessibilityState={{ disabled: !valid || saving, busy: saving }}
        {...stateAria({ disabled: !valid || saving, busy: saving })}
        disabled={!valid || saving}
        onPress={() => void save()}
        style={({ pressed }) => [
          card.secondaryAction,
          (!valid || saving) && card.disabled,
          pressed && card.secondaryActionPressed,
        ]}
      >
        {saving ? (
          <ActivityIndicator color={theme.palette.ink} />
        ) : (
          <Text style={card.secondaryActionText}>
            {t("mobile.account.passwordLabel", { defaultValue: "CHANGE PASSWORD" })}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s4 },
    fieldRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    // Layout only — box internals live in the <TextField> primitive.
    input: {
      flex: 1,
      minHeight: webTapFloor ?? 44,
    },
    // Layout only — box internals live in the <TextField> primitive.
    pwLabel: { marginTop: s.s3 },
    pwInput: {
      marginTop: s.s2,
    },
    inlineBtn: {
      minHeight: webTapFloor ?? 44,
      paddingHorizontal: s.s4,
      borderRadius: r.r3,
      backgroundColor: p.brand500,
      alignItems: "center",
      justifyContent: "center",
    },
    inlineBtnDisabled: { opacity: 0.4 },
    inlineBtnPressed: { opacity: 0.8 },
    inlineBtnText: { color: p.inkOnBrand, fontSize: t.sizes.base, fontWeight: "600" },
    error: { color: p.danger, fontSize: t.sizes.base },
    ok: { color: p.complete, fontSize: t.sizes.sm, fontWeight: "600" },
  });
}
